import React, { useState, useEffect, useMemo } from 'react';
import './HierarchicalProcedureSelector.css'; // Reuse the same CSS file
import DrugSelector from './DrugSelector';

// API configuration - Railway uses full URL, Codespace uses relative URLs with proxy
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface StandardSurgicalProcedure {
  id: number;
  name: string;
  description: string;
  standard_sop: string;
  keywords: string[];
}

interface SurgicalProcedureCategory {
  name: string;
  displayName: string;
  procedures: StandardSurgicalProcedure[];
  totalCount: number;
}

interface SurgicalProcedureSelection {
  procedureId: number;
  surgeryTypes: string[]; // ['non_survival', 'survival', 'multi_survival']
}

// Additional interfaces for extended surgical form data
interface SurgicalFormData {
  survivalTimetable: string;
  surgicalPrep: string;
  asepticTechniques: string[];
  woundClosure: string;
  sutureRemoval: string;
  surgeryLocation: string;
  personnel: PersonnelEntry[];
  anesthetics: AnestheticEntry[];
  monitoringMethods: string[];
  monitoringFrequency: string;
  paralyticsUsed: boolean;
  postopCare: {
    first24Hours: string;
    second24Hours: string;
    thereafter: string;
  };
  analgesia: AnalgesiaEntry[];
}

interface PersonnelEntry {
  id: string;
  name: string;
  trainingCategory: string;
  specificTraining: string;
}

interface AnestheticEntry {
  id: string;
  drug: string;
  dose: string;
  frequency: string;
  route: string;
  otherRoute: string;
}

interface AnalgesiaEntry {
  id: string;
  drug: string;
  dose: string;
  frequency: string;
  route: string;
  otherRoute?: string;
}

interface HierarchicalSurgicalProcedureSelectorProps {
  protocolId: number;
  onSelectionChange: (selections: SurgicalProcedureSelection[]) => void;
  onGenerateText: () => void;
  isLoading?: boolean;
  // Props for managing surgical description in proper order
  surgicalDescription?: string;
  onDescriptionChange?: (description: string) => void;
  onDescriptionSave?: () => void;
  isSaving?: boolean;
}

const HierarchicalSurgicalProcedureSelector: React.FC<HierarchicalSurgicalProcedureSelectorProps> = ({
  protocolId,
  onSelectionChange,
  onGenerateText,
  isLoading = false,
  surgicalDescription = '',
  onDescriptionChange,
  onDescriptionSave,
  isSaving: _isSaving // Accept but don't use - component has internal isSaving state
}) => {
  const [categories, setCategories] = useState<SurgicalProcedureCategory[]>([]);
  const [selections, setSelections] = useState<Map<number, string[]>>(new Map()); // procedureId -> surgery types
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [loadingStandard, setLoadingStandard] = useState(true);
  const [loadingSelected, setLoadingSelected] = useState(true);
  
  // AI review state
  const [aiReviewResult, setAiReviewResult] = useState<string>('');
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  
  // Extended form state - start with procedures and details expanded
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set()); // Start with all sections expanded
  const [formData, setFormData] = useState<SurgicalFormData>({
    survivalTimetable: '',
    surgicalPrep: '',
    asepticTechniques: [],
    woundClosure: '',
    sutureRemoval: '',
    surgeryLocation: '',
    personnel: [],
    anesthetics: [],
    monitoringMethods: [],
    monitoringFrequency: '',
    paralyticsUsed: false,
    postopCare: {
      first24Hours: '',
      second24Hours: '',
      thereafter: ''
    },
    analgesia: []
  });
  
  // Curated data from database
  const [surgeryLocations, setSurgeryLocations] = useState<any[]>([]);
  const [monitoringOptions, setMonitoringOptions] = useState<any[]>([]);
  const [trainingCategories, setTrainingCategories] = useState<any[]>([]);
  const [anestheticProtocols, setAnestheticProtocols] = useState<any[]>([]);
  
  // Species selection for drug selector
  const [selectedSpecies, setSelectedSpecies] = useState<'M' | 'R'>('M');

  // Load standard surgical procedures from backend
  useEffect(() => {
    const loadStandardProcedures = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        
        if (!token) {
          console.error('No auth token found');
          setLoadingStandard(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/surgical-procedures/standard`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setCategories(data.categories || []);
        } else {
          console.error('Failed to load surgical procedures:', response.statusText);
        }
      } catch (error) {
        console.error('Error loading surgical procedures:', error);
      } finally {
        setLoadingStandard(false);
      }
    };

    loadStandardProcedures();
  }, []);

  // Load existing selections from backend
  useEffect(() => {
    const loadSelectedProcedures = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        
        if (!token || !protocolId) {
          setLoadingSelected(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/surgical-procedures/selected/${protocolId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const newSelections = new Map<number, string[]>();
          
          data.selections?.forEach((selection: any) => {
            newSelections.set(selection.procedureId, selection.surgeryTypes);
          });
          
          setSelections(newSelections);
        } else {
          console.error('Failed to load selected surgical procedures:', response.statusText);
        }
      } catch (error) {
        console.error('Error loading selected surgical procedures:', error);
      } finally {
        setLoadingSelected(false);
      }
    };

    const loadFormData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        
        if (!token || !protocolId) {
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/surgical-procedures/load-form-data/${protocolId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.formData) {
            setFormData(data.formData);
            console.log('✅ Q4.5 form data loaded from database:', data.formData);
          }
        } else {
          console.error('Failed to load Q4.5 form data:', response.statusText);
        }
      } catch (error) {
        console.error('Error loading Q4.5 form data:', error);
      }
    };

    if (protocolId) {
      loadSelectedProcedures();
      loadFormData();
    }
  }, [protocolId]);

  // Load curated data from database
  useEffect(() => {
    const loadCuratedData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Load all curated data in parallel
        const [locationsRes, monitoringRes, trainingRes, anestheticsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/surgical-curated/locations`, { headers }),
          fetch(`${API_BASE_URL}/api/surgical-curated/monitoring`, { headers }),
          fetch(`${API_BASE_URL}/api/surgical-curated/training`, { headers }),
          fetch(`${API_BASE_URL}/api/surgical-curated/anesthetics`, { headers })
        ]);

        if (locationsRes.ok) {
          const data = await locationsRes.json();
          setSurgeryLocations(data.locations || []);
        }
        
        if (monitoringRes.ok) {
          const data = await monitoringRes.json();
          setMonitoringOptions(data.methods || []);
        }
        
        if (trainingRes.ok) {
          const data = await trainingRes.json();
          setTrainingCategories(data.categories || []);
        }
        
        if (anestheticsRes.ok) {
          const data = await anestheticsRes.json();
          setAnestheticProtocols(data.protocols || []);
        }
      } catch (error) {
        console.error('Error loading curated data:', error);
      }
    };

    loadCuratedData();
  }, []);

  // Toggle section collapse/expand
  const toggleSectionCollapse = (sectionName: string) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(sectionName)) {
      newCollapsed.delete(sectionName);
    } else {
      newCollapsed.add(sectionName);
    }
    setCollapsedSections(newCollapsed);
  };

  // Update form data helper
  const updateFormData = (field: keyof SurgicalFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Add personnel entry
  const addPersonnelEntry = () => {
    const newEntry: PersonnelEntry = {
      id: Date.now().toString(),
      name: '',
      trainingCategory: '',
      specificTraining: ''
    };
    updateFormData('personnel', [...formData.personnel, newEntry]);
  };

  // Remove personnel entry
  const removePersonnelEntry = (id: string) => {
    updateFormData('personnel', formData.personnel.filter(p => p.id !== id));
  };



  // AI assistance for individual sections
  const handleSectionAI = async (sectionType: string, currentContent: string) => {
    if (sectionType === 'comprehensive_review') {
      await handleComprehensiveReview();
    } else {
      // Placeholder for individual section AI calls
      console.log(`AI assistance requested for ${sectionType}:`, currentContent);
      alert(`AI assistance for ${sectionType} will be implemented with prompts in next session`);
    }
  };

  // Auto-save form data when it changes (optimized debouncing)
  const [lastSavedData, setLastSavedData] = useState<string>('');
  const [isSaving, setSaving] = useState(false);
  
  useEffect(() => {
    const currentDataString = JSON.stringify(formData);
    
    // Skip if data hasn't changed or if we're currently saving
    if (currentDataString === lastSavedData || isSaving) {
      return;
    }
    
    const timeoutId = setTimeout(async () => {
      // Only auto-save if we have actual data and protocol ID
      const hasData = protocolId && (
        formData.survivalTimetable?.trim() ||
        formData.surgicalPrep?.trim() ||
        (formData.asepticTechniques && formData.asepticTechniques.length > 0) ||
        (formData.personnel && formData.personnel.length > 0) ||
        (formData.anesthetics && formData.anesthetics.length > 0) ||
        (formData.analgesia && formData.analgesia.length > 0) ||
        formData.postopCare?.first24Hours?.trim() ||
        formData.postopCare?.second24Hours?.trim() ||
        formData.postopCare?.thereafter?.trim()
      );
      
      if (hasData) {
        setSaving(true);
        const result = await saveFormData();
        if (result.success) {
          setLastSavedData(currentDataString);
        }
        setSaving(false);
      }
    }, 3000); // 3 second debounce (increased from 2s)

    return () => clearTimeout(timeoutId);
  }, [formData, protocolId, lastSavedData, isSaving]);

  // Comprehensive AI review for entire Q4.5 section  
  const handleComprehensiveReview = async () => {
    setIsReviewLoading(true);
    setAiReviewResult(''); // Clear previous results
    try {
      // Collect all surgical data for review
      const surgicalData = {
        selectedProcedures: Array.from(selections.entries()).map(([procedureId, surgeryTypes]) => {
          const procedure = categories.flatMap(c => c.procedures).find(p => p.id === procedureId);
          const category = categories.find(c => c.procedures.some(p => p.id === procedureId));
          return {
            id: procedureId,
            name: procedure?.name || 'Unknown',
            surgeryTypes,
            category: category?.name || 'Unknown'
          };
        }),
        surgicalDescription,
        formData
      };

      const response = await fetch(`${API_BASE_URL}/api/surgical-procedures/comprehensive-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          protocolId,
          surgicalData
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Comprehensive review API error:', errorText);
        throw new Error('Failed to get comprehensive review');
      }

      const result = await response.json();
      
      // Comprehensive review completed successfully
      
      // Store the review result to display inline
      if (result.success) {
        setAiReviewResult(result.aiReview || result.message || 'Review completed successfully');
      } else {
        throw new Error(result.message || 'Review failed');
      }
      
    } catch (error) {
      console.error('Error in comprehensive review:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setAiReviewResult(`❌ Comprehensive Review Error:\n\n${errorMessage}\n\nPlease try again or contact support if the issue persists.`);
    } finally {
      setIsReviewLoading(false);
    }
  };

  // Save Q4.5 form data to database
  const saveFormData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token || !protocolId) {
        console.error('No auth token or protocol ID');
        return { success: false, error: 'Authentication required' };
      }

      const response = await fetch(`${API_BASE_URL}/api/surgical-procedures/save-form-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          protocolId,
          formData
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Q4.5 form data saved:', data);
        return { success: true, data };
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to save form data:', errorData);
        return { success: false, error: errorData.error || 'Save failed' };
      }
    } catch (error) {
      console.error('❌ Save form data error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  // Save section data (now actually saves to database)
  const handleSectionSave = async (sectionType: string, currentContent: string) => {
    console.log(`Saving ${sectionType} data to database...`);
    const result = await saveFormData();
    
    if (result.success) {
      alert(`✅ ${sectionType} data saved successfully!`);
    } else {
      alert(`❌ Failed to save ${sectionType} data: ${result.error}`);
    }
  };

  // Render section save button only (removed individual AI assistance)
  const renderSectionActions = (sectionType: string, currentContent: string, isLoading: boolean = false) => {
    return (
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '16px' }}>
        <button
          onClick={() => handleSectionSave(sectionType, currentContent)}
          disabled={isLoading || !currentContent.trim()}
          className="primary-button"
        >
          {isLoading ? '💾 Saving...' : 'Save Answer'}
        </button>
      </div>
    );
  };

  // Handle surgery type toggle for a procedure
  const handleSurgeryTypeToggle = async (procedureId: number, surgeryType: string, isChecked: boolean) => {
    const currentTypes = selections.get(procedureId) || [];
    let newTypes: string[];

    if (isChecked) {
      newTypes = [...currentTypes, surgeryType];
    } else {
      newTypes = currentTypes.filter(type => type !== surgeryType);
    }

    const newSelections = new Map(selections);
    if (newTypes.length === 0) {
      newSelections.delete(procedureId);
    } else {
      newSelections.set(procedureId, newTypes);
    }

    setSelections(newSelections);

    // Convert to array format for API and parent component
    const selectionsArray: SurgicalProcedureSelection[] = Array.from(newSelections.entries()).map(([id, types]) => ({
      procedureId: id,
      surgeryTypes: types
    }));

    // Save to backend immediately
    try {
      const token = localStorage.getItem('auth_token');
      if (token && protocolId) {
        await fetch(`${API_BASE_URL}/api/surgical-procedures/select`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            protocolId,
            selections: selectionsArray
          })
        });
      }
    } catch (error) {
      console.error('Error saving selections:', error);
    }

    // Notify parent component
    onSelectionChange(selectionsArray);
  };

  // Filter procedures based on search and show-only-selected
  const filteredCategories = useMemo(() => {
    return categories.map(category => {
      let filteredProcedures = category.procedures;

      // Apply search filter
      if (searchTerm) {
        filteredProcedures = filteredProcedures.filter(proc => 
          proc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (proc.description && proc.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
          proc.keywords?.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      // Apply show-only-selected filter
      if (showOnlySelected) {
        filteredProcedures = filteredProcedures.filter(proc => selections.has(proc.id));
      }

      return {
        ...category,
        procedures: filteredProcedures,
        totalCount: filteredProcedures.length
      };
    }).filter(category => category.procedures.length > 0);
  }, [categories, searchTerm, showOnlySelected, selections]);

  // Toggle category collapse/expand
  const toggleCategoryCollapse = (categoryName: string) => {
    const newCollapsed = new Set(collapsedCategories);
    if (newCollapsed.has(categoryName)) {
      newCollapsed.delete(categoryName);
    } else {
      newCollapsed.add(categoryName);
    }
    setCollapsedCategories(newCollapsed);
  };


  const totalAvailable = categories.reduce((sum, cat) => sum + cat.procedures.length, 0);
  const totalSelected = selections.size;
  
  // Smart detection of multiple survival procedures
  const multiSurvivalProcedures = useMemo(() => {
    const procedures: StandardSurgicalProcedure[] = [];
    categories.forEach(category => {
      category.procedures.forEach(procedure => {
        const procedureTypes = selections.get(procedure.id) || [];
        if (procedureTypes.includes('multi_survival')) {
          procedures.push(procedure);
        }
      });
    });
    return procedures;
  }, [categories, selections]);
  
  const hasMultiSurvival = multiSurvivalProcedures.length > 0;
  
  // Auto-open survival section if multi-survival procedures detected
  useEffect(() => {
    if (hasMultiSurvival && collapsedSections.has('survival')) {
      const newCollapsed = new Set(collapsedSections);
      newCollapsed.delete('survival');
      setCollapsedSections(newCollapsed);
    }
  }, [hasMultiSurvival, collapsedSections]);

  if (loadingStandard || loadingSelected) {
    return <div className="procedure-selector-loading">Loading surgical procedures...</div>;
  }

  // Component to render collapsible section header
  const renderSectionHeader = (sectionKey: string, title: string, icon: string, isComplete: boolean = false) => {
    const isCollapsed = collapsedSections.has(sectionKey);
    return (
      <div 
        className="surgical-section-header" 
        onClick={() => toggleSectionCollapse(sectionKey)}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          padding: '12px 16px', 
          backgroundColor: '#f8f9fa', 
          borderBottom: '1px solid #e9ecef', 
          cursor: 'pointer',
          fontWeight: '600',
          fontSize: '14px'
        }}
      >
        <span style={{ marginRight: '8px' }}>{isCollapsed ? '▶' : '▼'}</span>
        <span style={{ marginRight: '8px' }}>{icon}</span>
        <span style={{ flex: 1 }}>{title}</span>
        {isComplete && <span style={{ color: '#28a745', fontSize: '12px' }}>✓ Complete</span>}
      </div>
    );
  };

  return (
    <div className="hierarchical-procedure-selector">
      
      {/* Section 1: Procedure Selection */}
      {renderSectionHeader('procedures', 'Surgical Procedure Selection', '🔬', totalSelected > 0)}
      {!collapsedSections.has('procedures') && (
        <div style={{ padding: '16px', backgroundColor: 'white' }}>
          {/* Search and filter controls */}
          <div className="search-section">
            <input
              type="text"
              className="procedure-search-input"
              placeholder="Search surgical procedures..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <label className="show-selected-checkbox">
                <input
                  type="checkbox"
                  checked={showOnlySelected}
                  onChange={(e) => setShowOnlySelected(e.target.checked)}
                />
                Show only selected ({totalSelected})
              </label>
              
              <div style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                Select surgery types: <strong>NS</strong> = Non-Survival, <strong>S</strong> = Survival, <strong>MS</strong> = Multiple Survival
              </div>
            </div>
          </div>

          {/* Procedure categories */}
          <div className="procedure-categories">
            {filteredCategories.map(category => {
              const isCollapsed = collapsedCategories.has(category.name);
              const selectedInCategory = category.procedures.filter(proc => selections.has(proc.id)).length;

              return (
                <div key={category.name} className="procedure-category">
                  <div className="category-header" onClick={() => toggleCategoryCollapse(category.name)}>
                    <h4 className="category-title">
                      {category.displayName}
                      <span className="category-count">
                        ({selectedInCategory}/{category.procedures.length})
                      </span>
                    </h4>
                  </div>
                  
                  {!isCollapsed && (
                    <div className="category-procedures">
                      {category.procedures.map(procedure => {
                        const procedureTypes = selections.get(procedure.id) || [];
                        return (
                          <div key={procedure.id} className="procedure-item">
                            <div className="procedure-info">
                              <div className="procedure-name">{procedure.name}</div>
                              {procedure.description && (
                                <div className="procedure-description">
                                  {procedure.description}
                                </div>
                              )}
                            </div>
                            <div className="surgery-type-buttons">
                              <button
                                className={`surgery-type-btn ${procedureTypes.includes('non_survival') ? 'selected' : ''}`}
                                onClick={() => handleSurgeryTypeToggle(procedure.id, 'non_survival', !procedureTypes.includes('non_survival'))}
                                title="Non-Survival Surgery"
                              >
                                NS
                              </button>
                              <button
                                className={`surgery-type-btn ${procedureTypes.includes('survival') ? 'selected' : ''}`}
                                onClick={() => handleSurgeryTypeToggle(procedure.id, 'survival', !procedureTypes.includes('survival'))}
                                title="Survival Surgery"
                              >
                                S
                              </button>
                              <button
                                className={`surgery-type-btn ${procedureTypes.includes('multi_survival') ? 'selected' : ''}`}
                                onClick={() => handleSurgeryTypeToggle(procedure.id, 'multi_survival', !procedureTypes.includes('multi_survival'))}
                                title="Multiple Survival Surgery"
                              >
                                MS
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Generate Procedure Text Button and Description - RIGHT AFTER procedure selection */}
      {totalSelected > 0 && (
        <div className="generate-and-description-section">
          <div className="procedure-selector-actions" style={{ marginTop: '16px', padding: '16px', borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button 
                onClick={onGenerateText}
                disabled={isLoading}
                className="accent-button"
              >
                {isLoading ? 'Generating...' : `Generate Procedure Text (${totalSelected})`}
              </button>
            </div>
            <div className="help-text" style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic', marginTop: '8px', textAlign: 'center' }}>
              Generate description text from selected procedures
            </div>
          </div>
          
          {/* Surgical Description Section - properly positioned */}
          <div className="surgical-description-section" style={{ padding: '16px', backgroundColor: 'white', borderBottom: '1px solid #dee2e6' }}>
            <h5 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Surgical Description</h5>
            <p style={{ fontSize: '12px', color: '#6c757d', marginBottom: '12px', fontStyle: 'italic' }}>
              Use the "Generate Procedure Text" button above to auto-populate this section with your selected procedures, 
              then edit and add any additional procedures or details specific to your protocol.
            </p>
            <textarea
              value={surgicalDescription}
              onChange={(e) => onDescriptionChange?.(e.target.value)}
              placeholder="Provide detailed description of surgical procedures including prep, techniques, monitoring, and post-operative care..."
              rows={10}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '13px',
                minHeight: '200px',
                resize: 'vertical'
              }}
            />
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
              <button 
                onClick={onDescriptionSave}
                disabled={isSaving || !surgicalDescription?.trim()}
                className="primary-button"
              >
                {isSaving ? '💾 Saving...' : 'Save Answer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section 2: Multiple Survival Surgery */}
      {renderSectionHeader('survival', 'Multiple Survival Surgery', '📋', hasMultiSurvival ? formData.survivalTimetable.trim() !== '' : true)}
      {!collapsedSections.has('survival') && (
        <div style={{ padding: '16px', backgroundColor: 'white' }}>
          {hasMultiSurvival ? (
            <div>
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeaa7', 
                borderRadius: '4px',
                fontSize: '13px'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: '#856404' }}>
                  ⚠️ Multiple Survival Surgery Detected
                </div>
                <div style={{ color: '#856404' }}>
                  The following procedures are marked as Multiple Survival (MS):
                </div>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#856404' }}>
                  {multiSurvivalProcedures.map(proc => (
                    <li key={proc.id} style={{ marginBottom: '4px' }}>
                      <strong>{proc.name}</strong>
                      {proc.description && <span style={{ fontWeight: 'normal' }}> - {proc.description}</span>}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                  Sequence/Timetable and Justification for Multiple Survival Surgeries:
                </label>
                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px', fontStyle: 'italic' }}>
                  Address: Are surgeries performed under the same anesthetic period OR will animals recover from anesthesia prior to successive surgery? What recovery period is allowed between surgeries?
                </div>
                <textarea
                  style={{ width: '100%', minHeight: '100px', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '13px' }}
                  placeholder="Provide detailed sequence/timetable of the multiple survival surgeries listed above and justifications. Address whether surgeries are performed under same anesthetic or with recovery periods..."
                  value={formData.survivalTimetable}
                  onChange={(e) => updateFormData('survivalTimetable', e.target.value)}
                />
                {renderSectionActions('survival_timetable', formData.survivalTimetable)}
              </div>
            </div>
          ) : (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#6c757d', 
              fontStyle: 'italic',
              backgroundColor: '#f8f9fa',
              border: '1px dashed #dee2e6',
              borderRadius: '4px'
            }}>
              No multiple survival (MS) procedures selected. This section will automatically appear when you select procedures marked as "MS" in the procedure selector above.
            </div>
          )}
        </div>
      )}

      {/* Section 3: Surgical Details */}
      {renderSectionHeader('details', 'Surgical Details', '🔬', false)}
      {!collapsedSections.has('details') && (
        <div style={{ padding: '16px', backgroundColor: 'white' }}>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                Surgical prep of the animals:
              </label>
              <textarea
                style={{ width: '100%', minHeight: '60px', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '13px' }}
                placeholder="Describe surgical preparation procedures..."
                value={formData.surgicalPrep}
                onChange={(e) => updateFormData('surgicalPrep', e.target.value)}
              />
              {renderSectionActions('surgical_prep', formData.surgicalPrep)}
            </div>
            
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                Wound closure:
              </label>
              <textarea
                style={{ width: '100%', minHeight: '60px', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '13px' }}
                placeholder="Describe wound closure techniques..."
                value={formData.woundClosure}
                onChange={(e) => updateFormData('woundClosure', e.target.value)}
              />
              {renderSectionActions('wound_closure', formData.woundClosure)}
            </div>
            
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                Suture removal plan:
              </label>
              <textarea
                style={{ width: '100%', minHeight: '60px', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '13px' }}
                placeholder="Describe when and how sutures will be removed..."
                value={formData.sutureRemoval}
                onChange={(e) => updateFormData('sutureRemoval', e.target.value)}
              />
              {renderSectionActions('suture_removal', formData.sutureRemoval)}
            </div>
          </div>
        </div>
      )}

      {/* Section 4: Location & Personnel */}
      {renderSectionHeader('location', 'Location & Personnel', '👥', formData.surgeryLocation !== '' && formData.personnel.length > 0)}
      {!collapsedSections.has('location') && (
        <div style={{ padding: '16px', backgroundColor: 'white' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
              Where will surgeries be performed?
            </label>
            <select
              style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '13px' }}
              value={formData.surgeryLocation}
              onChange={(e) => updateFormData('surgeryLocation', e.target.value)}
            >
              <option value="">Select surgery location...</option>
              <option value="Surgical Suite A">Surgical Suite A</option>
              <option value="Surgical Suite B">Surgical Suite B</option>
              <option value="Procedure Room 1">Procedure Room 1</option>
              <option value="Other">Other (specify in notes)</option>
            </select>
          </div>
          
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <label style={{ fontWeight: '600', fontSize: '13px' }}>
                Personnel and Training:
              </label>
              <button
                onClick={addPersonnelEntry}
                style={{ 
                  background: '#FDD023', 
                  color: '#3E1C84', 
                  border: 'none', 
                  borderRadius: '4px', 
                  padding: '6px 12px', 
                  fontSize: '12px', 
                  fontWeight: '500', 
                  cursor: 'pointer' 
                }}
              >
                Add Personnel
              </button>
            </div>
            
            {formData.personnel.map(person => (
              <div key={person.id} style={{ 
                border: '1px solid #e9ecef', 
                borderRadius: '4px', 
                padding: '12px', 
                marginBottom: '8px',
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Name"
                    value={person.name}
                    onChange={(e) => {
                      const updated = formData.personnel.map(p => 
                        p.id === person.id ? { ...p, name: e.target.value } : p
                      );
                      updateFormData('personnel', updated);
                    }}
                    style={{ padding: '6px 8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '12px' }}
                  />
                  <select
                    value={person.trainingCategory}
                    onChange={(e) => {
                      const updated = formData.personnel.map(p => 
                        p.id === person.id ? { ...p, trainingCategory: e.target.value } : p
                      );
                      updateFormData('personnel', updated);
                    }}
                    style={{ padding: '6px 8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '12px' }}
                  >
                    <option value="">Select training category...</option>
                    <option value="Rodent Surgery Certification">Rodent Surgery Certification</option>
                    <option value="Graduate Student - Supervised">Graduate Student - Supervised</option>
                    <option value="Other">Other</option>
                  </select>
                  <button
                    onClick={() => removePersonnelEntry(person.id)}
                    style={{ 
                      background: '#dc3545', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      padding: '4px 8px', 
                      fontSize: '12px', 
                      cursor: 'pointer' 
                    }}
                  >
                    Remove
                  </button>
                </div>
                <textarea
                  placeholder="Specific training and qualifications..."
                  value={person.specificTraining}
                  onChange={(e) => {
                    const updated = formData.personnel.map(p => 
                      p.id === person.id ? { ...p, specificTraining: e.target.value } : p
                    );
                    updateFormData('personnel', updated);
                  }}
                  style={{ 
                    width: '100%', 
                    marginTop: '8px', 
                    padding: '6px 8px', 
                    border: '1px solid #ced4da', 
                    borderRadius: '4px', 
                    fontSize: '12px',
                    minHeight: '50px'
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 5: Anesthesia */}
      {renderSectionHeader('anesthesia', '4.5.4 Anesthesia', '💉', formData.anesthetics.length > 0)}
      {!collapsedSections.has('anesthesia') && (
        <div style={{ padding: '16px', backgroundColor: 'white' }}>
          <div style={{ 
            marginBottom: '16px', 
            fontSize: '0.9rem', 
            padding: '12px', 
            backgroundColor: 'rgba(253, 208, 35, 0.08)', 
            borderRadius: '4px', 
            color: '#50555B', 
            fontStyle: 'italic' 
          }}>
            Select from facility-approved anesthetic agents or add custom drugs. If using the same drug with multiple routes, add it multiple times with different route information.
          </div>
          
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontWeight: '600', fontSize: '0.9rem' }}>Species:</label>
            <select
              value={selectedSpecies}
              onChange={(e) => setSelectedSpecies(e.target.value as 'M' | 'R')}
              style={{ 
                padding: '6px 12px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px', 
                fontSize: '0.9rem',
                backgroundColor: 'white'
              }}
            >
              <option value="M">Mice</option>
              <option value="R">Rats</option>
            </select>
          </div>
          
          <DrugSelector
            category="anesthetics"
            species={selectedSpecies}
            entries={formData.anesthetics.map(a => ({ ...a, otherRoute: a.otherRoute || '' }))}
            onEntriesChange={(entries) => updateFormData('anesthetics', entries)}
          />
          
          {renderSectionActions('anesthesia', JSON.stringify(formData.anesthetics))}
        </div>
      )}

      {/* Section 6: Anesthetic Monitoring */}
      {renderSectionHeader('monitoring', '4.5.5 Anesthetic Monitoring', '👁️', formData.monitoringMethods.length > 0 || formData.monitoringFrequency.trim() !== '')}
      {!collapsedSections.has('monitoring') && (
        <div style={{ padding: '16px', backgroundColor: 'white' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
              Check method(s) for anesthetic monitoring:
            </label>
            
            {[
              'Palpebral reflex',
              'Toe pinch withdrawal', 
              'Stable respiration',
              'Color of mucous membranes',
              'Reflective eye color (in albino animals)',
              'Other'
            ].map(method => (
              <div key={method} style={{ marginBottom: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.monitoringMethods.includes(method)}
                    onChange={(e) => {
                      const updated = e.target.checked 
                        ? [...formData.monitoringMethods, method]
                        : formData.monitoringMethods.filter(m => m !== method);
                      updateFormData('monitoringMethods', updated);
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  {method}
                </label>
              </div>
            ))}
          </div>
          
          <div>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '1rem' }}>
              Frequency of monitoring:
            </label>
            <input
              type="text"
              placeholder="Describe monitoring frequency (e.g., every 15 minutes during anesthesia)"
              value={formData.monitoringFrequency}
              onChange={(e) => updateFormData('monitoringFrequency', e.target.value)}
              style={{ 
                width: '100%', 
                padding: '8px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px', 
                fontSize: '0.9rem' 
              }}
            />
          </div>
          
          {renderSectionActions('monitoring', formData.monitoringMethods.join(', ') + ' | ' + formData.monitoringFrequency)}
        </div>
      )}

      {/* Section 7: Paralytic Agents */}
      {renderSectionHeader('paralytics', '4.5.6 Paralytic Agents', '🚫', true)}
      {!collapsedSections.has('paralytics') && (
        <div style={{ padding: '16px', backgroundColor: 'white' }}>
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: 'rgba(253, 208, 35, 0.08)', 
            borderRadius: '4px',
            fontSize: '0.9rem',
            color: '#50555B'
          }}>
            <strong>Important:</strong> Surgery or painful procedures must not be performed on unanesthetized animals that have received paralytic chemical agents. 
            When utilized, neuromuscular blocking agents have the ability to eliminate signs (e.g. respiration) and reflexes (e.g. pedal response, palpebral reflex) 
            that are typically used to assess anesthetic depth. Autonomic nervous system changes (e.g. sudden change in heart rate or blood pressure) may indicate 
            pain or inadequate anesthetic depth and thus should be part of the monitoring plan.
          </div>
          
          <div>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
              Will paralytics be used?
            </label>
            <div style={{ display: 'flex', gap: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="paralytics"
                  checked={formData.paralyticsUsed === true}
                  onChange={() => updateFormData('paralyticsUsed', true)}
                  style={{ marginRight: '6px' }}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="paralytics"
                  checked={formData.paralyticsUsed === false}
                  onChange={() => updateFormData('paralyticsUsed', false)}
                  style={{ marginRight: '6px' }}
                />
                No
              </label>
            </div>
          </div>
          
          {renderSectionActions('paralytics', formData.paralyticsUsed ? 'Yes' : 'No')}
        </div>
      )}

      {/* Section 8: Postoperative Care and Monitoring */}
      {renderSectionHeader('postop_care', '4.5.7 Postoperative Care and Monitoring', '🏥', Object.values(formData.postopCare).some(value => value.trim() !== ''))}
      {!collapsedSections.has('postop_care') && (
        <div style={{ padding: '16px', backgroundColor: 'white' }}>
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: 'rgba(253, 208, 35, 0.08)', 
            borderRadius: '4px',
            fontSize: '0.9rem',
            color: '#50555B'
          }}>
            In addition to monitoring the animals, providing analgesics, and observing the surgical incision site until suture removal, 
            the IACUC encourages the use of non-pharmacologic methods of support to facilitate pain management 
            (e.g. administration of warm subcutaneous fluids; quiet recovery room; warm incubator during recovery from anesthesia; 
            fresh/dry bedding; extra nesting material; warm subcutaneous fluid support; availability of moist/palatable food).
          </div>
          
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '1rem' }}>
                First 24 Hours:
              </label>
              <textarea
                style={{ width: '100%', minHeight: '80px', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem' }}
                placeholder="Describe care and monitoring during the first 24 hours post-surgery..."
                value={formData.postopCare.first24Hours}
                onChange={(e) => updateFormData('postopCare', { ...formData.postopCare, first24Hours: e.target.value })}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '1rem' }}>
                Second 24 Hours:
              </label>
              <textarea
                style={{ width: '100%', minHeight: '80px', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem' }}
                placeholder="Describe care and monitoring during the second 24 hours post-surgery..."
                value={formData.postopCare.second24Hours}
                onChange={(e) => updateFormData('postopCare', { ...formData.postopCare, second24Hours: e.target.value })}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '1rem' }}>
                Thereafter:
              </label>
              <textarea
                style={{ width: '100%', minHeight: '80px', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem' }}
                placeholder="Describe ongoing care and monitoring after 48 hours..."
                value={formData.postopCare.thereafter}
                onChange={(e) => updateFormData('postopCare', { ...formData.postopCare, thereafter: e.target.value })}
              />
            </div>
          </div>
          
          {renderSectionActions('postop_care', Object.values(formData.postopCare).join(' | '))}
        </div>
      )}

      {/* Section 9: Pre- and Postoperative Analgesia */}
      {renderSectionHeader('analgesia', '4.5.8 Pre- and Postoperative Analgesia', '💊', formData.analgesia.length > 0)}
      {!collapsedSections.has('analgesia') && (
        <div style={{ padding: '16px', backgroundColor: 'white' }}>
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: 'rgba(253, 208, 35, 0.08)', 
            borderRadius: '4px',
            fontSize: '0.9rem',
            color: '#50555B'
          }}>
            <strong>Important:</strong> Investigators should assume that procedures that cause pain in humans also cause pain in animals. 
            Federal guidelines require that procedures be conducted in a manner which avoids or minimizes discomfort, pain or distress. 
            Select from facility-approved analgesic agents or add custom drugs for pain management.
          </div>
          
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontWeight: '600', fontSize: '0.9rem' }}>Species:</label>
            <select
              value={selectedSpecies}
              onChange={(e) => setSelectedSpecies(e.target.value as 'M' | 'R')}
              style={{ 
                padding: '6px 12px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px', 
                fontSize: '0.9rem',
                backgroundColor: 'white'
              }}
            >
              <option value="M">Mice</option>
              <option value="R">Rats</option>
            </select>
          </div>
          
          <DrugSelector
            category="analgesics"
            species={selectedSpecies}
            entries={formData.analgesia.map(a => ({ ...a, otherRoute: a.otherRoute || '' }))}
            onEntriesChange={(entries) => updateFormData('analgesia', entries)}
          />
          
          {renderSectionActions('analgesia', JSON.stringify(formData.analgesia))}
        </div>
      )}

      {/* Final AI Review Button - moved to the end */}
      <div className="final-actions" style={{ marginTop: '20px', padding: '16px', borderTop: '2px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <button 
            onClick={() => handleSectionAI('comprehensive_review', 'all_sections')}
            disabled={isReviewLoading}
            className="accent-button"
            title="AI review of all surgical sections for consistency and completeness"
          >
            {isReviewLoading ? 'Getting AI Review...' : 'Final AI Review'}
          </button>
          <div className="help-text" style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic', marginTop: '8px' }}>
            Final AI review of all surgical sections for consistency and completeness
          </div>
        </div>
        
        {/* AI Review Results - inline display matching other questions */}
        {aiReviewResult && (
          <div 
            className="ai-suggestion"
            style={{
              marginLeft: '0',
              marginRight: '0',
              width: '100%',
              maxWidth: 'none'
            }}
          >
            <h4>🤖 AI Comprehensive Review Results</h4>
            <div className="suggestion-content">
              <textarea
                className="ai-edit-textarea"
                value={aiReviewResult}
                onChange={(e) => setAiReviewResult(e.target.value)}
                style={{ 
                  minHeight: '300px', 
                  resize: 'both',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  width: '100%'
                }}
                placeholder="AI review results will appear here..."
              />
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                You can edit this review as needed. The review analyzes all surgical sections for completeness and IACUC compliance.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HierarchicalSurgicalProcedureSelector;